import { useRouter } from "next/router";
import React, { useEffect } from "react";
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";

import { Button, Card, Disclosure } from "@chef/components";
import { BlockContent, ITellAFriendCompetition } from "@chef/feature-sanity";
import { usePersistentState } from "@chef/hooks";
import { BRAND_PATHS } from "@chef/constants";
import {
  selectCompetitionDisclosureOpen,
  toggleCompetitionDisclosureOpen,
} from "@chef/state-management";
import { useTrack } from "@chef/state-management/hooks";

import { useCompetitionResultsData } from "../../../hooks/useCompetitionResultsData";
import { CircularProgressWithStatus } from "./CircularProgressWithStatus";
import { CompetitionStatus } from "./CompetitionStatus";
import { useCompetitionDateValidationData } from "../../../hooks/useCompetitionDateValidationData";

import { intl } from "./Competition.Intl";

interface TellAFriendCompetitionProps {
  competitionSetting: ITellAFriendCompetition["competitionSetting"];
  competitionBlock: React.ReactNode;
  howCompetitionWorks: React.ReactNode;
  competitionPrize: React.ReactNode;
  scrollToInvitation: () => void;
  scrollRef: React.RefObject<HTMLDivElement>;
}

export const TellAFriendCompetition = ({
  competitionSetting,
  competitionBlock,
  howCompetitionWorks,
  competitionPrize,
  scrollToInvitation,
  scrollRef,
}: TellAFriendCompetitionProps) => {
  const router = useRouter();
  const dispatch = useDispatch();

  const openDisclosure = useSelector(selectCompetitionDisclosureOpen);

  const track = useTrack();

  const [hasClaimedPrize, setHasClaimedPrize] = usePersistentState(
    "hasClaimedPrize",
    false,
  );

  const competitionRequirements = competitionSetting?.competitionRequirements;

  const dateSetting = competitionSetting?.dateSetting;

  const competitionStatusText = competitionSetting?.competitionStatusText;

  const { invitesRequired, deliveriesRequired } = competitionRequirements;

  const { deliveryCutoffDate, endDate, startDate } = dateSetting;

  const {
    competitionResults: {
      isCompetitionWon,
      isCompetitionLost,
      hasAchievedTargetInvites,
      hasAchievedTargetDeliveries,
    },
    current: { currentInvitations, currentDeliveries },
    isRecruitmentDataLoading,
  } = useCompetitionResultsData({
    invitesRequired,
    deliveriesRequired,
    startDate,
    endDate,
    deliveryCutoffDate,
  });

  const { isCompetitionPeriod, isResultsAccessPeriod } =
    useCompetitionDateValidationData({
      startDate,
      endDate,
      deliveryCutoffDate,
    });

  const handleShowPrizeInformation = () => {
    if (isCompetitionWon) {
      setHasClaimedPrize(true);
    }
  };

  const handleClaimPrize = () => {
    track("claimedTellAFriendReward", {
      affiliation: "Frontend process",
      competition_start_date: startDate,
    });

    if (!hasClaimedPrize && isCompetitionWon && !isRecruitmentDataLoading) {
      setHasClaimedPrize(true);
    }

    router.push(BRAND_PATHS.MY_MENU_PAGE);
  };

  useEffect(() => {
    if (!isCompetitionWon && !isRecruitmentDataLoading) {
      setHasClaimedPrize(false);
    }
  }, [startDate, isCompetitionWon, isRecruitmentDataLoading]);

  let competitionStatus;

  if (isCompetitionWon) {
    competitionStatus = intl.READY;
  } else if (isCompetitionLost) {
    competitionStatus = intl.ENDED;
  }

  if (!isResultsAccessPeriod) {
    return null;
  }

  return (
    <Card className="mt-5">
      <div className="relative h-auto mt-8">
        {hasClaimedPrize ? (
          competitionPrize
        ) : (
          <CircularProgressWithStatus
            competitionSetting={competitionSetting}
            competitionStatusText={competitionStatusText}
          />
        )}
      </div>

      <div className="px-6 text-center">
        {isCompetitionLost &&
          intl.COMPETITION_ENDED_BUT_THERE_WILL_BE_ANOTHER_ONE_SOON}
        {!isCompetitionLost && !isCompetitionWon && (
          <BlockContent body={competitionSetting.deliveriesRequiredText} />
        )}
        {isCompetitionWon && (
          <Button
            full
            primary
            onClick={
              hasClaimedPrize ? handleClaimPrize : handleShowPrizeInformation
            }
            className="mt-4"
          >
            {hasClaimedPrize
              ? intl.PLAN_UPCOMING_DELIVERIES
              : intl.CALIM_YOUR_REWARD}
          </Button>
        )}
      </div>

      <div className="border-t-1.5 border-grey-1 mt-6 pt-4 text-sm">
        <div className="flex items-center justify-between">
          <div className="mb-2 text-sm font-medium">{intl.YOUR_STATUS}</div>
          <strong
            className={clsx(
              isCompetitionLost && "text-error",
              isCompetitionWon && "text-secondary",
            )}
          >
            {competitionStatus}
          </strong>
        </div>

        {invitesRequired > 0 && (
          <CompetitionStatus
            isRequirementMet={hasAchievedTargetInvites}
            requiredCount={invitesRequired}
            currentCount={currentInvitations}
          >
            <strong>{intl.INVITE_NUM_OF_FRIENDS(invitesRequired)}</strong>
          </CompetitionStatus>
        )}

        {deliveriesRequired > 0 && (
          <CompetitionStatus
            isRequirementMet={hasAchievedTargetDeliveries}
            requiredCount={deliveriesRequired}
            currentCount={currentDeliveries}
          >
            <strong>
              {intl.AT_LEAST_NUM_OF_DELIVERIES_NEEDED(deliveriesRequired)}
            </strong>
          </CompetitionStatus>
        )}
      </div>

      {competitionBlock}
      <div className="mt-3" ref={scrollRef}>
        <Disclosure
          Title={() => (
            <strong className="text-base">{intl.HOW_COMPETITION_WORKS}</strong>
          )}
          open={openDisclosure}
          onClick={() => dispatch(toggleCompetitionDisclosureOpen())}
        >
          {howCompetitionWorks}

          {isCompetitionPeriod && (
            <Button
              primary
              className="w-full my-6 full"
              onClick={scrollToInvitation}
            >
              <strong className="text-center">
                {intl.RECRUIT_FRIENDS_AND_WIN}
              </strong>
            </Button>
          )}
        </Disclosure>
      </div>
    </Card>
  );
};
